import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { FORM_FIELDS, PATHS } from '../../config';
import useErrorHandler from '../../utils/useErrorHandler';
import useSchema from '../../schema/useSchema';
import useRegions from '../../utils/useRegions';

/**
 *
 * @param {object} contestData
 * @param {string} expId
 * @returns {Promise<any>}
 */
const submitToExb = async (contestData, canonLink = '') => {
  const api = process.env.REACT_APP_SUBMIT_API;
  const data = {
    teamId: process.env.REACT_APP_TEAM_ID,
    expId: process.env.REACT_APP_CONTEST_EXP_ID,
    tintId: process.env.REACT_APP_CONTEST_TINT_ID,
    canonLink,
    payload: contestData,
  };
  return await axios.post(api, data);
};

const getFilteredData = (data, keys) =>
  Object.entries(data).reduce((result, current) => {
    const [key, val] = current;
    if (keys.includes(key)) result[key] = val;
    return result;
  }, {});

const sanitizeContestData = (data, t) => {
  const {
    PROFILE_STATEMENT,
    PRINT_STATEMENT,
    PHOTO_SKILLS,
    VIDEO_SKILLS,
    MAIN_GENRE,
    MEDIA,
    DATA_PRIVACY_POLICY,
    MARKETING_CONSENT,
    TRANSFER_CONSENT,
    AGE_STATEMENT,
    PROFESSION_STATEMENT,
    PRODUCT_STATEMENT,
    PHOTOGALLERY_CONSENT,
    TERMS_AND_CONDITIONS,
  } = FORM_FIELDS;
  data[PROFILE_STATEMENT] = t(data[PROFILE_STATEMENT], { ns: 'translation', lng: 'en' });
  data[PRINT_STATEMENT] = t(data[PRINT_STATEMENT], { ns: 'translation', lng: 'en' });
  data[PHOTO_SKILLS] = t(data[PHOTO_SKILLS], { ns: 'translation', lng: 'en' });
  data[VIDEO_SKILLS] = t(data[VIDEO_SKILLS], { ns: 'translation', lng: 'en' });
  data[MAIN_GENRE] = data[MAIN_GENRE].map((e) => t(e, { ns: 'translation', lng: 'en' }));
  data[AGE_STATEMENT] = t(data[AGE_STATEMENT], { ns: 'translation', lng: 'en' });
  data[PROFESSION_STATEMENT] = t(data[PROFESSION_STATEMENT], { ns: 'translation', lng: 'en' });
  data[MEDIA] = JSON.stringify(data[MEDIA]);
  data[MAIN_GENRE] = JSON.stringify(data[MAIN_GENRE]);
  data[PRODUCT_STATEMENT] = JSON.stringify(data[PRODUCT_STATEMENT]);
  data[DATA_PRIVACY_POLICY] = String(data[DATA_PRIVACY_POLICY]);
  data[TERMS_AND_CONDITIONS] = String(data[TERMS_AND_CONDITIONS]);
  data[MARKETING_CONSENT] = String(data[MARKETING_CONSENT]);
  data[PHOTOGALLERY_CONSENT] = String(data[PHOTOGALLERY_CONSENT]);
  if (data[TRANSFER_CONSENT]) {
    data[TRANSFER_CONSENT] = String(data[TRANSFER_CONSENT]);
  }
  // TODO: Remove that once problem with form_submission is solved
  data['form_submission'] = '{}';
  return data;
};

const useSubmitForm = () => {
  const { lang } = useParams();
  const { Schema } = useSchema();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const errorHandler = useErrorHandler();
  const { getCanonPageLink } = useRegions();
  const { t } = useTranslation(['countries', 'translation']);

  const submitForm = async (data) => {
    const { LANGUAGE } = FORM_FIELDS;
    data[LANGUAGE] = lang || pathname.split('/')?.[1];

    const contestData = sanitizeContestData(getFilteredData(data, Schema.getContestKeys()), t);
    try {
      await submitToExb(contestData, getCanonPageLink());
      navigate(`../${PATHS.COMPLETED}`, { replace: true, relative: 'path' });
    } catch (error) {
      errorHandler(error);
    }
  };

  return { submitForm };
};

export default useSubmitForm;
